import {useMemo} from 'react'
import type {
  CheckoutLineFragment,
  ProductVariant,
} from 'src/api/saleor/generated'
import type {VariantObject} from 'src/components/products/ProductCard/interfaces'
import {
  getMediaDataVariantObjects,
  getMediaDataVariants,
  getVariant,
} from 'src/components/products/ProductCard/utils'
import {ProductTypePiece} from 'src/components/products/ProductCartPicker/interfaces'
import {allocateItemsToBoxes} from 'src/utils/checkout'
import styled from 'styled-components'
import {CheckoutLineVariant} from './CheckoutLineVariant'
import {useProduct} from './hooks/useProduct'

const Wrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
`

export const CheckoutLineVariants = ({
  line,
  qty,
  variantId,
}: {
  qty: number | null
  line: CheckoutLineFragment
  variantId: string
}): JSX.Element | null => {
  const {error, loading, data} = useProduct({line})
  const items: Array<VariantObject & {quantity: number}> = useMemo(() => {
    if (error || loading || !data?.product?.variants) {
      return []
    }
    const variants = data.product.variants
      ? getMediaDataVariants(
          getVariant(data.product.variants as ProductVariant[])()?.metadata,
        )
      : {}
    const objects = getMediaDataVariantObjects(variants) || {}
    const mappedObject = Object.keys(objects)
      .map((key) => ({
        ...objects[key],
        id: objects[key].id || ProductTypePiece,
      }))
      .sort((a, b) => b.qty_per_unit - a.qty_per_unit)
    const boxes = mappedObject.map((item) => [item.id, item.qty_per_unit]) as [
      string,
      number,
    ][]
    if (boxes.length === 0 || qty === 0) {
      return []
    }
    if (qty == null) {
      return []
    }
    const allocate = allocateItemsToBoxes(qty, boxes)
    return mappedObject.map((item) => ({
      ...item,
      quantity: allocate[item.id] ?? 0,
    }))
  }, [data?.product?.variants, error, loading, qty])
  if (loading) {
    // TODO: Loading screen
    return null
  }
  if (error) {
    // TODO: Error screen
    return null
  }
  return (
    <>
      <Wrapper>
        {[...items].reverse().map((item) => (
          <CheckoutLineVariant
            key={item.id}
            item={item}
            qty={qty}
            variantId={variantId}
          />
        ))}
      </Wrapper>
    </>
  )
}
